import App from 'App';
import { useEffect, useRef } from 'react';

import { QueryClient, useQueryClient } from 'react-query';
import {
  MicrositeApiContext,
  MicrositeApiContextProvider,
} from 'contexts/MicrositeApiContext';
import { IntlShape, useIntl } from 'react-intl';
import { AsyncDictionaryProvider } from '@higo/common/lib/features/Dictionary';
import { useAppLocale } from '@higo/common/lib/features/Locale';
import { LocaleDefinition } from '@higo/common/lib/features/Locale/localeDefinition';
import { createAcceptLanguageInterceptor } from '@higo/common/lib/ext/axios/interceptors';
import { createBaseAxios } from 'utils/axios';

const apiContextSetup = (
  intl: IntlShape,
  queryClient: QueryClient,
  getLanguage: () => string,
): MicrositeApiContext => {
  const acceptLanguageInterceptor =
    createAcceptLanguageInterceptor(getLanguage);

  const baseAxios = createBaseAxios();

  const apiContext = {
    baseAxios,
  };

  baseAxios.interceptors.request.use(acceptLanguageInterceptor);

  return apiContext;
};

export const AppWrapper = () => {
  const { current: currentLocaleDefinition } = useAppLocale();
  const localeDefinitionRef = useRef<LocaleDefinition>(currentLocaleDefinition);
  const queryClient = useQueryClient();
  const intl = useIntl();

  const apiContext = useRef(
    apiContextSetup(intl, queryClient, () => localeDefinitionRef.current.code),
  );

  useEffect(() => {
    localeDefinitionRef.current = currentLocaleDefinition;
  }, [currentLocaleDefinition]);

  return (
    <MicrositeApiContextProvider value={apiContext.current}>
      <AsyncDictionaryProvider
        axios={apiContext.current.baseAxios}
        languageCode={currentLocaleDefinition.code}
      >
        <App />
      </AsyncDictionaryProvider>
    </MicrositeApiContextProvider>
  );
};
